import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Trash, User as UserIcon, Prohibit, CheckCircle, UploadSimple } from "@phosphor-icons/react";
import { toast, Toaster } from "sonner";

const ROLE_LABELS = {
  super_admin: "Super Admin",
  company_admin: "Company Admin",
  dispatcher: "Dispatcher",
  user: "Viewer",
  warehouse: "Warehouse",
  warehouse_super: "Kanal Super Warehouse",
  warehouse_super_l1: "Kanal Super Warehouse · Level 1",
  warehouse_super_l2: "Kanal Super Warehouse · Level 2",
};

const ROLE_COLORS = {
  super_admin: "bg-zinc-900 text-white",
  company_admin: "bg-[#002BF6]/10 text-[#002BF6] border border-[#002BF6]/20",
  dispatcher: "bg-amber-50 text-amber-700 border border-amber-200",
  user: "bg-zinc-100 text-zinc-700 border border-zinc-200",
  warehouse: "bg-emerald-50 text-emerald-700 border border-emerald-200",
  warehouse_super: "bg-fuchsia-50 text-fuchsia-700 border border-fuchsia-200",
  warehouse_super_l1: "bg-fuchsia-50 text-fuchsia-700 border border-fuchsia-200",
  warehouse_super_l2: "bg-fuchsia-100 text-fuchsia-800 border border-fuchsia-300 font-semibold",
};

const EMPTY = { email: "", password: "", name: "", role: "user", company_id: "" };

export default function Users() {
  const { user: current } = useAuth();
  const [rows, setRows] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [filterCompany, setFilterCompany] = useState("all");

  const load = async () => {
    const params = filterCompany !== "all" && current.role === "super_admin"
      ? { company_id: filterCompany }
      : {};
    const { data } = await api.get("/users", { params });
    setRows(data);
    if (current.role === "super_admin") {
      const c = await api.get("/companies");
      setCompanies(c.data);
    }
  };

  useEffect(() => { load(); }, [filterCompany]);

  const roleOptions = current.role === "super_admin"
    ? ["super_admin", "company_admin", "dispatcher", "user", "warehouse", "warehouse_super_l1", "warehouse_super_l2"]
    : ["company_admin", "dispatcher", "user", "warehouse"];

  const save = async () => {
    try {
      const payload = { ...form };
      if (current.role === "company_admin") delete payload.company_id;
      if (payload.role === "super_admin") payload.company_id = null;
      else if (!payload.company_id) delete payload.company_id;
      await api.post("/users", payload);
      toast.success("User added");
      setOpen(false);
      setForm(EMPTY);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    if (!confirm("Remove this user?")) return;
    try {
      await api.delete(`/users/${id}`);
      toast.success("User removed");
      load();
    } catch (e) {
      toast.error("Failed");
    }
  };

  const updateRole = async (u, newRole) => {
    if (newRole === u.role) return;
    try {
      await api.patch(`/users/${u.id}/role`, { role: newRole });
      toast.success(`Role updated to ${ROLE_LABELS[newRole]}`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const toggleBlock = async (u) => {
    const target = !u.is_blocked;
    const verb = target ? "block" : "unblock";
    if (!confirm(`${target ? "Block" : "Unblock"} ${u.name}? ${target ? "They will be signed out and unable to log in." : "They will be able to log in again."}`)) return;
    try {
      await api.patch(`/users/${u.id}/block`, { is_blocked: target });
      toast.success(`${u.name} ${target ? "blocked" : "unblocked"}`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || `Failed to ${verb} user`);
    }
  };

  const companyName = (id) => companies.find(c => c.id === id)?.name || "—";

  const importRef = React.useRef(null);

  const importCSV = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    const params = {};
    // super_admin can either include company_id column per row, or pre-select via filter
    if (current.role === "super_admin" && filterCompany !== "all") {
      params.company_id = filterCompany;
    }
    try {
      const { data } = await api.post("/users/import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        params,
      });
      const errMsg = (data.errors || []).length ? ` · ${data.errors.length} error${data.errors.length === 1 ? "" : "s"}` : "";
      toast.success(`Imported ${data.created} user${data.created === 1 ? "" : "s"}${data.skipped ? ` · ${data.skipped} skipped` : ""}${errMsg}`);
      if ((data.errors || []).length) {
        // eslint-disable-next-line no-console
        console.warn("User import errors:", data.errors);
      }
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Import failed");
    } finally {
      if (importRef.current) importRef.current.value = "";
    }
  };

  const isSuper = current.role === "super_admin";

  // Super admin sees rows grouped by company; everyone else sees a flat list
  const grouped = useMemo(() => {
    if (!isSuper) return null;
    const map = new Map();
    for (const u of rows) {
      const key = u.company_id || "__no_company__";
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(u);
    }
    const groups = [];
    const sortedCompanies = [...companies].sort((a, b) => a.name.localeCompare(b.name));
    for (const c of sortedCompanies) {
      if (map.has(c.id)) groups.push({ key: c.id, label: c.name, users: map.get(c.id) });
    }
    if (map.has("__no_company__")) {
      groups.push({ key: "__no_company__", label: "Super admins / Unassigned", users: map.get("__no_company__") });
    }
    return groups;
  }, [isSuper, rows, companies]);

  const COL_COUNT = isSuper ? 6 : 5;
  const renderRow = (u) => (
    <tr key={u.id} className="border-b border-zinc-100 last:border-0 hover:bg-zinc-50" data-testid={`user-row-${u.id}`}>
      <td className="px-5 py-4">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-zinc-200 flex items-center justify-center text-zinc-700">
            <UserIcon size={14} weight="bold" />
          </div>
          <div className="font-medium text-zinc-900">{u.name}</div>
        </div>
      </td>
      <td className="px-5 py-4 text-zinc-700">{u.email}</td>
      <td className="px-5 py-4">
        {u.id === current.id ? (
          <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold ${ROLE_COLORS[u.role]}`}>
            {ROLE_LABELS[u.role]} (you)
          </span>
        ) : (
          <Select value={u.role} onValueChange={(v) => updateRole(u, v)}>
            <SelectTrigger className="w-40 h-8" data-testid={`role-select-${u.id}`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {roleOptions.map(r => (<SelectItem key={r} value={r}>{ROLE_LABELS[r]}</SelectItem>))}
            </SelectContent>
          </Select>
        )}
      </td>
      <td className="px-5 py-4">
        {u.is_blocked ? (
          <span className="inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold bg-red-50 text-red-700 border border-red-200" data-testid={`status-blocked-${u.id}`}>
            <Prohibit size={11} weight="bold" /> Blocked
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200" data-testid={`status-active-${u.id}`}>
            <CheckCircle size={11} weight="bold" /> Active
          </span>
        )}
      </td>
      {isSuper && (
        <td className="px-5 py-4 text-zinc-700">{u.company_id ? companyName(u.company_id) : "—"}</td>
      )}
      <td className="px-5 py-4 text-right">
        {u.id !== current.id && (current.role === "super_admin" || (current.role === "company_admin" && u.role !== "super_admin")) && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => toggleBlock(u)}
            data-testid={`toggle-block-${u.id}`}
            title={u.is_blocked ? "Unblock user" : "Block user"}
            className={u.is_blocked ? "text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50" : "text-amber-600 hover:text-amber-700 hover:bg-amber-50"}
          >
            {u.is_blocked ? <CheckCircle size={15} weight="bold" /> : <Prohibit size={15} weight="bold" />}
          </Button>
        )}
        {u.id !== current.id && (
          <Button variant="ghost" size="sm" onClick={() => remove(u.id)} data-testid={`delete-user-${u.id}`} className="text-red-600 hover:text-red-700 hover:bg-red-50">
            <Trash size={15} />
          </Button>
        )}
      </td>
    </tr>
  );

  return (
    <div className="p-6 sm:p-8 max-w-[1400px]" data-testid="users-page">
      <Toaster position="top-right" />
      <div className="flex flex-col sm:flex-row sm:items-baseline sm:justify-between gap-4 mb-8">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-zinc-500">Access Control</div>
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight mt-1" style={{ fontFamily: "Work Sans" }}>
            Team Members
          </h1>
        </div>
        <div className="flex gap-2">
          {current.role === "super_admin" && (
            <Select value={filterCompany} onValueChange={setFilterCompany}>
              <SelectTrigger className="w-56" data-testid="filter-company"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All companies</SelectItem>
                {companies.map(c => (<SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>))}
              </SelectContent>
            </Select>
          )}
          <input
            type="file"
            accept=".csv"
            ref={importRef}
            className="hidden"
            onChange={(e) => e.target.files?.[0] && importCSV(e.target.files[0])}
            data-testid="import-users-input"
          />
          <Button
            variant="outline"
            onClick={() => importRef.current?.click()}
            data-testid="import-users-btn"
            title="Bulk import users from CSV (columns: name,email,password,role[,company_id])"
          >
            <UploadSimple size={15} className="mr-1.5" /> Import CSV
          </Button>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="bg-[#002BF6] hover:bg-[#0021C7] text-white" data-testid="add-user-btn">
                <Plus size={16} className="mr-1.5" weight="bold" /> New User
              </Button>
            </DialogTrigger>
            <DialogContent
              className="w-[calc(100vw-2rem)] sm:w-full sm:max-w-lg p-0 max-h-[90vh] gap-0 overflow-hidden flex flex-col"
              data-testid="user-dialog"
            >
              <DialogHeader className="px-5 sm:px-6 pt-5 pb-3 border-b border-zinc-100 shrink-0">
                <DialogTitle>Invite New User</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 overflow-y-auto px-5 sm:px-6 py-4 flex-1" data-testid="user-dialog-body">
                <Field label="Full name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testId="user-name" />
                <Field label="Email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testId="user-email" />
                <Field label="Password" type="password" value={form.password} onChange={(v) => setForm({ ...form, password: v })} testId="user-password" />
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-600">Role</Label>
                  <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                    <SelectTrigger className="mt-1.5" data-testid="user-role"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {roleOptions.map(r => (<SelectItem key={r} value={r}>{ROLE_LABELS[r]}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
                {current.role === "super_admin" && form.role !== "super_admin" && (
                  <div>
                    <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-600">Company</Label>
                    <Select value={form.company_id} onValueChange={(v) => setForm({ ...form, company_id: v })}>
                      <SelectTrigger className="mt-1.5" data-testid="user-company"><SelectValue placeholder="Select..." /></SelectTrigger>
                      <SelectContent>
                        {companies.map(c => (<SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
              <DialogFooter className="px-5 sm:px-6 py-4 border-t border-zinc-100 bg-white shrink-0 gap-2 sm:gap-0">
                <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                <Button onClick={save} className="bg-[#002BF6] hover:bg-[#0021C7] text-white" data-testid="save-user-btn">Create</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="bg-white border border-zinc-200 rounded-md overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]" data-testid="users-table">
          <thead className="bg-zinc-50 border-b border-zinc-200">
            <tr className="text-left text-xs uppercase tracking-wider text-zinc-500">
              <th className="px-5 py-3 font-semibold">Name</th>
              <th className="px-5 py-3 font-semibold">Email</th>
              <th className="px-5 py-3 font-semibold">Role</th>
              <th className="px-5 py-3 font-semibold">Status</th>
              {current.role === "super_admin" && <th className="px-5 py-3 font-semibold">Company</th>}
              <th className="px-5 py-3 font-semibold text-right">Actions</th>
            </tr>
          </thead>
          <UsersTableBody
            rows={rows}
            grouped={grouped}
            renderRow={renderRow}
            colCount={COL_COUNT}
            isSuper={isSuper}
          />
        </table>
      </div>
    </div>
  );
}

function UsersTableBody({ rows, grouped, renderRow, colCount, isSuper }) {
  if (rows.length === 0) {
    return (
      <tbody>
        <tr><td colSpan={colCount} className="px-5 py-12 text-center text-zinc-500">No users.</td></tr>
      </tbody>
    );
  }
  if (!isSuper || !grouped) {
    return <tbody>{rows.map(renderRow)}</tbody>;
  }
  return (
    <tbody>
      {grouped.map((g) => (
        <React.Fragment key={g.key}>
          <tr className="bg-zinc-50/70 border-y border-zinc-200" data-testid={`user-group-${g.key}`}>
            <td colSpan={colCount} className="px-5 py-2 text-[11px] font-semibold uppercase tracking-[0.16em] text-zinc-600">
              {g.label}
              <span className="ml-2 text-zinc-400 font-normal normal-case tracking-normal">
                · {g.users.length} {g.users.length === 1 ? "user" : "users"}
              </span>
            </td>
          </tr>
          {g.users.map(renderRow)}
        </React.Fragment>
      ))}
    </tbody>
  );
}

function Field({ label, value, onChange, testId, type = "text" }) {
  return (
    <div>
      <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-600">{label}</Label>
      <Input type={type} className="mt-1.5" value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId} />
    </div>
  );
}
